/**
 * Shown when a bank-style quiz is completed — replaces the question
 * UI. Surfaces the user's score across the run + a Restart button.
 *
 * Restart resets bank position only; session stats (today / streak /
 * best) keep accumulating since they persist across reloads anyway.
 */

import type { SessionStats } from "@/features/quiz/lib/common";

interface QuizCompletePanelProps {
  bankSize: number;
  stats: SessionStats;
  onRestart: () => void;
}

export function QuizCompletePanel({ bankSize, stats, onRestart }: QuizCompletePanelProps) {
  // Today's accuracy reflects this user's session aggregate (which
  // includes the just-finished run). We slice the LAST `bankSize`
  // attempts for the run-specific scoreline.
  // (We don't have per-question history in state, so we report
  // today's totals + flag that this run completed.)
  const todayPct = stats.attempts === 0 ? 0 : Math.round((stats.correct / stats.attempts) * 100);

  return (
    <div className="text-center space-y-6 py-4">
      <div className="space-y-2">
        <div className="text-[10px] uppercase tracking-[0.14em] text-accent font-semibold">
          Quiz complete
        </div>
        <h2 className="text-2xl font-bold text-text tracking-tight">
          {bankSize} of {bankSize} answered
        </h2>
        <p className="text-sm text-text-muted">
          Today: <strong className="text-text">{stats.correct} / {stats.attempts}</strong> ({todayPct}%) across all attempts.
          {" "}
          {stats.streak > 0 && (
            <>Current streak: <strong className="text-text">{stats.streak}</strong>. </>
          )}
          Best: <strong className="text-text">{stats.bestStreak}</strong>.
        </p>
      </div>

      <button
        type="button"
        onClick={onRestart}
        autoFocus
        className="w-full py-3 rounded-xl bg-accent text-white font-medium hover:bg-accent/90 transition active:scale-[0.98]"
      >
        Restart quiz
      </button>
    </div>
  );
}
